import { DomainModel, ErrorList } from 'domains/models';
import { DateTime, getDateTime } from 'infras/datetime';
import { Role } from '../team_user/role';
import { PlanId } from './planId';
import { latestPlanVersion, PlanVersion } from './planVersion';
import { TrialChannel } from './trial_channel';

const unlimitedCount = 999999;
export const TRIAL_DAYS = 14;

interface Props {
  teamId: number;
  planId: PlanId;
  planVersion: PlanVersion;
  quantity: number;
  purchaseQuantity: number | null;
  scheduleQuantity: number | null;
  schedulePlanId: PlanId | null;
  schedulePlanVersion: PlanVersion | null;
  scheduleStripePriceId: string | null;
  stripePriceId?: string | null;
  stripeSubscriptionId?: string | null;
  trialStartedAt?: DateTime | null;
  trialEndedAt?: DateTime | null;
  trialChannel?: TrialChannel | null;
  initialPlanChoseAt?: DateTime | null;
  planChangedAt?: DateTime | null;
  quantityChangedAt?: DateTime | null;
  isAvailableRevealGuestUser: boolean;
}

export default class Subscription extends DomainModel {
  teamId: number;
  planId: PlanId;
  planVersion: PlanVersion;
  quantity: number;
  purchaseQuantity: number | null;
  scheduleQuantity: number | null;
  schedulePlanId: PlanId | null;
  schedulePlanVersion: PlanVersion | null;
  scheduleStripePriceId: string | null;
  stripePriceId: string | null;
  stripeSubscriptionId: string | null;
  trialStartedAt: DateTime | null;
  trialEndedAt?: DateTime | null;
  trialChannel?: TrialChannel | null;
  initialPlanChoseAt?: DateTime | null;
  planChangedAt?: DateTime | null;
  quantityChangedAt?: DateTime | null;
  isAvailableRevealGuestUser: boolean;

  constructor(props: Props) {
    super(props);
    this._className = 'Subscription';
    this.teamId = props.teamId;
    this.planId = props.planId;
    this.planVersion = props.planVersion;
    this.quantity = props.quantity;
    this.purchaseQuantity = props.purchaseQuantity;
    this.scheduleQuantity = props.scheduleQuantity;
    this.schedulePlanId = props.schedulePlanId;
    this.schedulePlanVersion = props.schedulePlanVersion;
    this.scheduleStripePriceId = props.scheduleStripePriceId;
    this.stripePriceId = props.stripePriceId || null;
    this.stripeSubscriptionId = props.stripeSubscriptionId || null;
    this.trialStartedAt = props.trialStartedAt || null;
    this.trialEndedAt = props.trialEndedAt || null;
    this.trialChannel = props.trialChannel || null;
    this.initialPlanChoseAt = props.initialPlanChoseAt || null;
    this.planChangedAt = props.planChangedAt || null;
    this.quantityChangedAt = props.quantityChangedAt || null;
    this.isAvailableRevealGuestUser = props.isAvailableRevealGuestUser;
  }

  static permited = [
    'planId',
    'planVersion',
    'stripePriceId',
    'stripeSubscriptionId',
    'quantity',
    'trialStartedAt',
    'trialEndedAt',
    'initialPlanChoseAt',
    'planChangedAt',
    'quantityChangedAt',
    'trialChannel',
    'purchaseQuantity',
    'scheduleQuantity',
    'schedulePlanId',
    'schedulePlanVersion',
    'scheduleStripePriceId',
  ];

  static permit(updater: any) {
    return super.permit(updater, this.permited);
  }

  isValid() {
    super.beforeValidate();
    let p = '';

    p = 'teamId';
    if (!this.teamId) {
      this.addError(p, 'E001');
    }

    p = 'planId';

    p = 'planVersion';
    if (
      this.planVersion < PlanVersion.V1 ||
      this.planVersion > latestPlanVersion
    ) {
      this.addError(p, 'E001');
    }

    p = 'stripeSubscriptionId';

    p = 'stripePriceId';

    p = 'quantity';
    if (!this.quantity) {
      this.addError(p, 'E001');
    } else {
      if (this.quantity < 1) {
        this.addError(p, 'E002');
      }
    }
    return super.isValid();
  }

  isOverQuantity(newUserCount: number) {
    const availableQuantity = this.scheduleQuantity || this.quantity;
    if (newUserCount > availableQuantity) {
      console.log(`${newUserCount} > ${availableQuantity}`);
      return true;
    }

    return false;
  }

  static maxPollCandidateTimes(planId: PlanId) {
    switch (planId) {
      case PlanId.Free:
      case PlanId.Pro:
      case PlanId.Team:
      case PlanId.Enterprice:
        return 40;
    }
  }

  static maxPollGuests(planId: PlanId) {
    switch (planId) {
      case PlanId.Free:
      case PlanId.Pro:
      case PlanId.Team:
      case PlanId.Enterprice:
        return 100;
    }
  }

  static maxQuantity(planId: PlanId) {
    return unlimitedCount;
  }
  static isValidQuantity(planId: PlanId, quantity: number) {
    const max = this.maxQuantity(planId);
    switch (planId) {
      case PlanId.Free: {
        if (quantity > max) return false;
        return true;
      }
      case PlanId.Pro: {
        if (quantity > max) return false;
        return true;
      }
      case PlanId.Team: {
        if (quantity > max) return false;
        return true;
      }
      case PlanId.Enterprice: {
        return true;
      }
      default:
    }
    return false;
  }

  static isJicooTrialing(
    trialChannel: TrialChannel | null | undefined,
    trialEndedAt: DateTime | null | undefined,
  ) {
    const now = getDateTime();
    return (
      trialChannel === TrialChannel.Jicoo &&
      !!trialEndedAt &&
      trialEndedAt > now
    );
  }

  static featureIntroductionFromTrialStart() {
    return 60 * 24;
  }
  static calendarIntegrationFromTrialStart() {
    return 60 * 24 * 2;
  }
  static expireRemindFromTrialEnd() {
    return 60 * 24;
  }

  static isShowIntegrationAdvancedMenu(planId: PlanId, role: Role) {
    return role <= Role.Admin;
  }

  static trialCampaignToken() {
    if (process.env['TRIAL_CAMPAIGN_TOKEN']) {
      return process.env['TRIAL_CAMPAIGN_TOKEN'];
    }
  }

  static isDefaultFreeTrial(
    trialEndedAt: string | null,
    trialChannel: TrialChannel | null,
  ) {
    return getDateTime(trialEndedAt) > getDateTime() && !!trialChannel;
  }
}
